import { HTML_BLOCK_MESSAGE_SOURCE } from '../../consts/html-block-policy'

/**
 * The height/print reporter injected into every HTML block frame.
 *
 * This is a frozen source string, not a serialized function, on purpose: Prodigy serves a
 * CSP without `'unsafe-inline'` in `script-src` (see CDK `lib/prodigy-frontend-stack.ts`),
 * and a srcdoc document inherits the embedder's policy. The only way to run this script is
 * to allowlist its SHA-256 in that CSP, which requires the injected text to be byte-identical
 * for every frame and across builds — so nothing frame-specific may be interpolated here and
 * a minifier must never get to rewrite it. The frame reads its own id from `window.name`
 * (set through the iframe's `name` attribute) and infers the measured root from the DOM.
 *
 * Editing this string changes its hash. `HTML_BLOCK_REPORTER_SHA256` and the CDK CSP entry
 * must be updated together; the unit test pins the value so a drift fails the suite.
 */
export const HTML_BLOCK_REPORTER_SOURCE = `(function (win) {
  var SOURCE = 'max-html-block';
  var id = win.name;
  var root = win.document.getElementById('__max_root');
  var target = root || win.document.body;
  var scheduled = false;
  var lastHeight = null;
  function reportHeight() {
    scheduled = false;
    var height = root
      ? root.getBoundingClientRect().height
      : (win.document.body ? win.document.body.scrollHeight : 0);
    if (height === 0) {
      scheduleReport();
      return;
    }
    if (lastHeight !== null && height === lastHeight) {
      return;
    }
    lastHeight = height;
    win.parent.postMessage({ source: SOURCE, id: id, height: height }, '*');
  }
  function scheduleReport() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    win.requestAnimationFrame(reportHeight);
  }
  win.addEventListener('load', scheduleReport);
  win.addEventListener('message', function (event) {
    if (event.source !== win.parent || !event.data || typeof event.data !== 'object') {
      return;
    }
    if (event.data.source !== SOURCE || event.data.id !== id || event.data.action !== 'print') {
      return;
    }
    win.print();
  });
  if (typeof win.ResizeObserver === 'function' && target) {
    new win.ResizeObserver(scheduleReport).observe(target);
  }
  scheduleReport();
})(window)`

/**
 * SHA-256 of HTML_BLOCK_REPORTER_SOURCE, base64. Mirrored in the CDK CSP `script-src`
 * as `'sha256-<value>'`.
 */
export const HTML_BLOCK_REPORTER_SHA256 = 'hn4cvBdUQ6GaUu+rOtRhiXtAeCH9u/THT46FSDIqD1s='

/** The frame reads its id from `window.name`; the parent owns the message source. */
export const HTML_BLOCK_REPORTER_MESSAGE_SOURCE = HTML_BLOCK_MESSAGE_SOURCE
